import type { ReactNode } from 'react'
import { Pause, Play, SkipBack, SkipForward, Square } from 'lucide-react'
import type { useReadAloud } from '@/a11y/useReadAloud'

interface Props {
  reader: ReturnType<typeof useReadAloud>
}

/** Compact player shown while the page is being read aloud. */
export function ReadAloudBar({ reader }: Props) {
  const { status, error, position, rate } = reader
  const playing = status === 'playing'
  if (error) return (
    <div role="alert" className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-50 rounded-xl bg-ink-deep p-4 text-surface shadow-xl sm:inset-x-auto sm:left-1/2 sm:w-96 sm:-translate-x-1/2">
      <p className="text-sm">{error}</p>
      <div className="mt-2 flex gap-3">
        <button type="button" onClick={reader.start} className="min-h-11 rounded-lg bg-surface px-4 font-bold text-ink-deep">Retry</button>
        <button type="button" onClick={reader.stop} className="min-h-11 rounded-lg px-4 font-bold underline">Dismiss</button>
      </div>
    </div>
  )
  return (
    <div
      role="region"
      aria-label="Read aloud controls"
      className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-50 flex animate-fade-up flex-col items-center justify-between gap-1 rounded-2xl sm:flex-row sm:rounded-full bg-ink-deep px-1.5 py-1.5 sm:pl-4 text-surface shadow-[0_18px_50px_-12px_rgb(0_0_0_/_0.5)] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2"
    >
      <p aria-live="polite" className="mr-1 whitespace-nowrap text-xs font-bold tabular text-surface/85">
        {status === 'starting' ? 'Starting' : playing ? 'Reading' : 'Paused'} {position.index + 1}/{position.total}
      </p>
      <div className="flex items-center">
        <BarButton label="Previous paragraph" onClick={reader.previous} disabled={position.index === 0}>
          <SkipBack className="h-5 w-5" aria-hidden />
        </BarButton>
        <BarButton label={status !== 'paused' ? 'Pause' : 'Resume'} onClick={status !== 'paused' ? reader.pause : reader.resume} primary>
          {status !== 'paused' ? <Pause className="h-5 w-5" aria-hidden /> : <Play className="h-5 w-5" aria-hidden />}
        </BarButton>
        <BarButton
          label="Next paragraph"
          onClick={reader.next}
          disabled={position.index >= position.total - 1}
        >
          <SkipForward className="h-5 w-5" aria-hidden />
        </BarButton>
        <BarButton label={`Reading speed ${rate}×, change`} onClick={reader.cycleRate}>
          <span className="text-xs font-extrabold tabular">{rate}×</span>
        </BarButton>
        <BarButton label="Stop reading" onClick={reader.stop}>
          <Square className="h-4 w-4" aria-hidden />
        </BarButton>
      </div>
    </div>
  )
}

function BarButton({
  label,
  onClick,
  disabled,
  primary,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  primary?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={
        'inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface ' +
        (primary ? 'bg-surface text-ink-deep hover:bg-gia-blue-light-3' : 'hover:bg-surface/15')
      }
    >
      {children}
    </button>
  )
}
